import { DateInput, TextInput, InfoBlock, Dropdown, MultiInput, PhoneInput, EmailInput, useTranslator, getLocale } from "@sia/style-guide";
import { useCallback, useContext, useEffect, useState } from "react";
import { WizarContext } from "contexts/WizardContext";
import { singlePersonalInfoActionCreator } from "contexts/WizardContext";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import GlobalContext from "contexts/GlobalContext";
import { initialWizardFormState } from "contexts/WizardContext";

const PersonalInfo = ({ errors, setErrors }) => {
  const translate = useTranslator();
  const { language } = useContext(GlobalContext);
  const { state, dispatch } = useContext(WizarContext);
  let personalInfo = state.single.personalInfo;
  let personalInfoErrors = errors.single.personalInfo;
  let changState = useCallback((payload) => dispatch(singlePersonalInfoActionCreator(payload)), [dispatch]);

  const resetInputFieldError = (property, error) => {
    const exculdedProperties = ["phone", "mobile", "email"];
    if (!exculdedProperties.includes(property)) {
      setErrors((errorsState) => ({
        ...errorsState,
        single: {
          ...errorsState.single,
          personalInfo: { ...errorsState.single.personalInfo, [property]: error },
        },
      }));
    } else {
      exculdedProperties.forEach((exculdedProperty) => {
        if (property === exculdedProperty)
          setErrors((errorsState) => ({
            ...errorsState,
            single: {
              ...errorsState.single,
              personalInfo: { ...errorsState.single.personalInfo, [exculdedProperty]: error },
            },
          }));
      });
    }
  };

  const [academicTitles, setAcademicTitle] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cantons, setCantons] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const data_academic = await webPlatformAdapter.request("GET", "rpc/get_academic_titles_list", null);
      let academicTitles = data_academic.map((e) => ({ ...e, label: e.name }));
      setAcademicTitle(academicTitles);
      const data_countries = await webPlatformAdapter.request("GET", "rpc/get_countries_list", { lang: getLocale(language) });
      let countries = data_countries.map((e) => ({ ...e, label: e.name }));
      setCountries(countries);
      const data_states = await webPlatformAdapter.request("GET", "rpc/get_states_list", { lang: getLocale(language) });
      let cantons = data_states.map((e) => ({ ...e, label: e.name }));
      setCantons(cantons);
    }
    fetchData();
  }, [language]);

  useEffect(() => {
    const fetchData = async () => {
      const data_customer = await webPlatformAdapter.getCustomerDataOfMemberTypeSingle(language);
      if (data_customer && data_customer.length > 0) {
        let data = data_customer[0];
        let gender = data.gender;
        let firstName = data.firstname;
        let lastName = data.lastname;
        let nationality = data.language;
        let diplomTitle = data.diploma_title;
        let email = data.login_email;
        changState({ gender, firstName, lastName, nationality, diplomTitle, email });
      }
    }
    fetchData();
  }, [language, changState]);

  const { user } = useContext(GlobalContext);
  const userId = user.siaId;

  useEffect(() => {
    if (userId) {
      pgAdapter.getWizardSession(({ data }) => {
        const defaultPersonInfo = { ...initialWizardFormState.single.personalInfo };
        const { personalInfo } = data?.single || {};
        const { dateOfBirth, language, academicTitle, additionalInfo, street, zip, city, country, canton } = personalInfo || defaultPersonInfo;
        changState({ dateOfBirth, language, academicTitle, additionalInfo, street, zip, city, country, canton });
      });
    }
  }, [userId, changState]);

  const setUserSession = (obj) => {
    pgAdapter.getWizardSession(({ data }) => {
      const updatedSession = { ...data, single: { ...(data.single || {}), personalInfo: { ...(data?.single?.personalInfo || {}), ...obj } } };
      pgAdapter.setWizardSession({ ...updatedSession });
    });
  };

  const languages = [
    { label: "DE", id: "de" },
    { label: "FR", id: "fr" },
    { label: "IT", id: "it" },
    { label: "EN", id: "en" },
  ];

  return (
    <div className="personal-info-page">
      <InfoBlock>
        <span className="label">{translate("wizard.form.single.personal-info.gender.label")}</span>
        <p>{personalInfo.gender}</p>
        <span className="label">{translate("wizard.form.single.personal-info.first-name.label")}</span>
        <p>{personalInfo.firstName}</p>
        <span className="label">{translate("wizard.form.single.personal-info.last-name.label")}</span>
        <p>{personalInfo.lastName}</p>
        <span className="label">{translate("wizard.form.single.personal-info.nationality.label")}</span>
        <p>{personalInfo.nationality}</p>
      </InfoBlock>
      <div className="sia-margin-03-top"></div>
      <DateInput
        onBlur={() => {
          if (personalInfo.dateOfBirth) {
            setUserSession({ dateOfBirth: personalInfo.dateOfBirth });
          }
        }}
        id={"personal-page-birth-date"}
        required={true}
        label={translate("wizard.form.single.personal-info.birth-date.label")}
        placeholder={"01.01.2000"}
        value={personalInfo.dateOfBirth}
        onChange={(event, value, error) => {
          resetInputFieldError("dateOfBirth", error);
          changState({ dateOfBirth: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: "d.m.yyyy" })}
        invalidText={personalInfoErrors.dateOfBirth}
      />
      <Dropdown
        id={"personal-page-language"}
        label={translate("wizard.form.single.personal-info.language.label")}
        items={languages}
        value={personalInfo.language}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("language");
          changState({ language: value });
          setUserSession({ language: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={personalInfoErrors.language}
        invalidText={personalInfoErrors.language}
      />
      <TextInput
        onBlur={() => {
          if (personalInfo.diplomTitle) {
            setUserSession({ diplomTitle: personalInfo.diplomTitle });
          }
        }}
        id={"personal-page-diplom-title"}
        value={personalInfo.diplomTitle}
        onChange={(event, value) => {
          resetInputFieldError("diplomTitle");
          changState({ diplomTitle: value });
        }}
        label={translate("wizard.form.single.personal-info.diplom-title.label")}
        required={true}
        invalidPatternText={personalInfoErrors.diplomTitle}
        invalidText={personalInfoErrors.diplomTitle}
      />
      <Dropdown
        id={"personal-page-academic-title"}
        label={translate("wizard.form.single.personal-info.academic-title.label")}
        items={academicTitles}
        value={personalInfo.academicTitle}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("academicTitle");
          changState({ academicTitle: value });
          setUserSession({ academicTitle: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalidPatternText={personalInfoErrors.academicTitle}
        invalidText={personalInfoErrors.academicTitle}
      />
      <p className="productive-heading-01 sia-padding-03-top sia-padding-03-bottom">{translate("wizard.form.single.personal-info.address.title")} </p>
      <TextInput
        onBlur={() => {
          if (personalInfo.additionalInfo) {
            setUserSession({ additionalInfo: personalInfo.additionalInfo });
          }
        }}
        id={"personal-page-additional-info"}
        value={personalInfo.additionalInfo}
        onChange={(event, value) => {
          resetInputFieldError("additionalInfo");
          changState({ additionalInfo: value });
        }}
        label={translate("wizard.form.single.personal-info.additional-info.label")}
        invalidPatternText={personalInfoErrors.additionalInfo}
        invalidText={personalInfoErrors.additionalInfo}
      />
      <TextInput
        onBlur={() => {
          if (personalInfo.street) {
            setUserSession({ street: personalInfo.street });
          }
        }}
        id={"personal-page-street"}
        value={personalInfo.street}
        placeholder={translate("wizard.form.single.personal-info.steet.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("street");
          changState({ street: value });
        }}
        label={translate("wizard.form.single.personal-info.street.label")}
        required={true}
        invalidPatternText={personalInfoErrors.street}
        invalidText={personalInfoErrors.street}
      />
      <TextInput
        onBlur={() => {
          if (personalInfo.zip) {
            setUserSession({ zip: personalInfo.zip });
          }
        }}
        id={"personal-page-zip"}
        value={personalInfo.zip}
        placeholder={translate("wizard.form.single.personal-info.zip.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("zip");
          changState({ zip: value });
        }}
        label={translate("wizard.form.single.personal-info.zip.label")}
        required={true}
        invalidPatternText={personalInfoErrors.zip}
        invalidText={personalInfoErrors.zip}
      />
      <TextInput
        onBlur={() => {
          if (personalInfo.city) {
            setUserSession({ city: personalInfo.city });
          }
        }}
        id={"personal-page-city"}
        value={personalInfo.city}
        placeholder={translate("wizard.form.single.personal-info.city.placeholder")}
        onChange={(event, value) => {
          resetInputFieldError("city");
          changState({ city: value });
        }}
        label={translate("wizard.form.single.personal-info.city.label")}
        required={true}
        invalidPatternText={personalInfoErrors.city}
        invalidText={personalInfoErrors.city}
      />
      <Dropdown
        id={"personal-page-country"}
        label={translate("wizard.form.single.personal-info.country.label")}
        items={countries}
        value={personalInfo.country}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("country");
          changState({ country: value });
          setUserSession({ country: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalidPatternText={personalInfoErrors.country}
        invalidText={personalInfoErrors.country}
      />
      <Dropdown
        id={"personal-page-canton"}
        label={translate("wizard.form.single.personal-info.canton.label")}
        items={cantons}
        value={personalInfo.canton}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("canton");
          changState({ canton: value });
          setUserSession({ canton: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalidPatternText={personalInfoErrors.canton}
        invalidText={personalInfoErrors.canton}
      />
      <MultiInput id={"contact"} invalidText={null} translate={translate}>
        <PhoneInput
          id={"personal-page-phone"}
          label={translate("wizard.form.single.personal-info.telephone.label")}
          value={personalInfo.phone}
          onChange={(event, value, error) => {
            resetInputFieldError("phone", error);
            changState({ phone: value });
          }}
          invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.phone") })}
          invalidText={personalInfoErrors.phone}
        />

        <PhoneInput
          id={"personal-page-mobile"}
          label={translate("wizard.form.single.personal-info.mobile.label")}
          value={personalInfo.mobile}
          onChange={(event, value, error) => {
            resetInputFieldError("mobile", error);
            changState({ mobile: value });
          }}
          invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.phone") })}
          invalidText={personalInfoErrors.mobile}
        />

        <EmailInput
          id={"personal-page-email"}
          label={translate("wizard.form.single.personal-info.email.label")}
          value={personalInfo.email}
          onChange={(event, value, error) => {
            resetInputFieldError("email", error);
            changState({ email: value });
          }}
          invalidPatternText={translate("form.input.error.invalid.with-example", { example: translate("form.input.error.invalid.example.email") })}
          invalidText={personalInfoErrors.email}
        />
      </MultiInput>
    </div>
  );
};

export default PersonalInfo;
